import { z } from "zod";

import { productSchema } from "./product";

const chatMessageRoleSchema = z.enum(["user", "assistant", "system", "tool"]);

export const chatMessageContentSchema = z
  .array(
    z.discriminatedUnion("type", [
      z.object({
        type: z.literal("text"),
        text: z.string(),
        // NOTE (Bobby: 2025-06-25): It is default text if not provided. This specifies the content inside the text field
        // and alternative content types like products can be parsed into a object like ChatProductItem
        contentType: z
          .enum([
            "text",
            "product",
            "buildError",
            "revert",
            "selectedTemplate",
            "generatedPage",
          ])
          .optional(),
        isHidden: z.boolean().optional(),
        isNewMessageGroup: z.boolean().optional(),
      }),
      z.object({
        type: z.literal("tool-call"),
        toolCallId: z.string(),
        toolName: z.string(),
        args: z.any(),
      }),
      z.object({
        type: z.literal("tool-result"),
        toolCallId: z.string(),
        toolName: z.enum([
          "codeChangeTool",
          "retryCodeChangeTool",
          "semanticSearchProductsTool",
          "prepareProductSectionScaffoldingTool",
          "injectBuildingProductSectionContextTool",
          "codeChangeSwarmTool",
          "getIntegrationConfigTool",
          "suggestTemplatesTool",
          "generatePageTool",
        ]),
        result: z.object({
          success: z.boolean().optional(),
          message: z.string().optional(),
          appliedDiffs: z.array(z.string()).optional(),
          fileVersionId: z.string().optional(),
          fileVersionSummary: z.string().optional(),
          buildingProductBoxesContext: z.string().optional(),
          suggestedTemplateIds: z.array(z.string()).optional(),
          selectedTemplateId: z.string().optional(),
          jobId: z.string().optional(),
          publicAccessToken: z.string().optional(),
          triggerJobType: z.string().optional(),
          error: z.string().optional(),
          createdPageId: z.string().optional(),
          isCompleted: z.boolean().optional(),
          integrationConfig: z.any().optional(),
          integrationPrompt: z.string().optional(),
        }),
      }),
      z.object({
        type: z.literal("image"),
        id: z.string().optional(),
        image: z.string(),
        isHidden: z.boolean().optional(),
      }),
    ]),
  )
  .describe("ChatMessageContent");

export const chatMessageSchema = z
  .object({
    id: z.string().uuid(),
    createdAt: z.coerce.date(),
    role: chatMessageRoleSchema,
    content: chatMessageContentSchema,
    chatId: z.string().uuid(),
  })
  .describe("ChatMessage");

export const contextObjectSchema = z
  .object({
    openPageId: z.string().optional().nullable(),
  })
  .describe("ContextObject");

export const chatProductItemSchema = productSchema
  .pick({
    id: true,
    title: true,
    description: true,
    status: true,
    featuredMedia: true,
    media: true,
    options: true,
    variants: true,
    sellingPlanGroups: true,
  })
  .describe("ChatProductItem");

export const chatBuildErrorItemSchema = z
  .object({
    type: z.literal("buildError"),
    isReverting: z.boolean(),
    erroredVersionId: z.string().uuid().optional(),
    hasRetried: z.boolean().optional(),
  })
  .describe("ChatBuildErrorItem");

export const chatAssetItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    fileExtension: z.string(),
  })
  .describe("ChatAssetItem");

export const chatRevertItemSchema = z
  .object({
    fileVersionId: z.string(),
    context: z.string().optional(),
  })
  .describe("ChatRevertItem");

export const chatSelectedTemplateItemSchema = z
  .object({
    type: z.literal("selectedTemplate"),
    selectedTemplateId: z.string(),
    selectedTemplateName: z.string(),
    fileId: z.string(),
  })
  .describe("ChatSelectedTemplateItem");

export const chatGeneratedPageItemSchema = z
  .object({
    type: z.literal("generatedPage"),
    pageName: z.string(),
    pageId: z.string(),
  })
  .describe("ChatGeneratedPageItem");
